import React, { FunctionComponent } from 'react'
import { Device, PrintQueue } from './BriefSectionTypes'
import { formatThousands } from './briefHelpers'
import styles from './BriefShell.module.css'

interface TileProps {
  tile: { key: string; label: string; value: string }
  valueSize?: 'sm' | 'md'
}

interface SharedSectionProps {
  renderTile: (tile: TileProps['tile'], valueSize?: TileProps['valueSize']) => React.ReactNode
  renderSectionIcon: (title: string) => React.ReactNode | null
}

export const PrintQueuesSection: FunctionComponent<SharedSectionProps & { queues: PrintQueue[] }> = ({
  queues,
  renderTile,
  renderSectionIcon,
}) => {
  if (queues.length === 0) {
    return null
  }

  const mostUsed = queues.at(0)
  const leastUsed = queues.at(-1)
  const totalPrints = formatThousands(queues.reduce((acc, curr) => acc + curr.totalPages, 0))

  return (
    <div className={styles.sectionWrapper} data-testid='print-queues-section'>
      <header className={styles.sectionHeader}>
        {renderSectionIcon('print queues') && (
          <figure className='angular'>
            <i className='icon thirtytwo flex-noshrink'>{renderSectionIcon('print queues')}</i>
          </figure>
        )}
        <h4 className={styles.sectionTitle}>Print queues</h4>
      </header>
      <div
        className={`${styles.metricGrid} ${styles.threeColumnMetricGrid} ${styles.sectionContainer}`}
        data-testid='print-queues-tiles'
      >
        {mostUsed && renderTile({ key: 'most-used', label: 'Most used', value: mostUsed.type }, 'sm')}
        {leastUsed && renderTile({ key: 'least-used', label: 'Least used', value: leastUsed.type }, 'sm')}
        {renderTile({ key: 'total-page-prints', label: 'Total page prints', value: totalPrints }, 'sm')}
      </div>
      <div className={`${styles.tableContainer} ${styles.sectionContainer}`}>
        <table className={`${styles.table} ${styles.printQueuesTable}`} aria-label='Print queues'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Model</th>
              <th className={styles.numericColumn}>Total pages</th>
              <th className={styles.numericColumn}>Color pages</th>
              <th className={styles.numericColumn}>Black &amp; white pages</th>
            </tr>
          </thead>
          <tbody>
            {queues.map(queue => (
              <tr key={queue.name}>
                <td>{queue.name}</td>
                <td>{queue.type}</td>
                <td className={styles.numericColumn}>{formatThousands(queue.totalPages)}</td>
                <td className={styles.numericColumn}>{formatThousands(queue.colorPages)}</td>
                <td className={styles.numericColumn}>{formatThousands(queue.bwPages)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export const DevicesSection: FunctionComponent<SharedSectionProps & { devices: Device[] }> = ({
  devices,
  renderTile,
  renderSectionIcon,
}) => {
  if (devices.length === 0) {
    return null
  }

  const mostUsed = devices.at(0)
  const leastUsed = devices.at(-1)
  const totalPages = devices.reduce((total, device) => total + device.totalPages, 0)

  return (
    <div className={styles.sectionWrapper} data-testid='devices-section'>
      <header className={styles.sectionHeader}>
        {renderSectionIcon('devices') && (
          <figure className='angular'>
            <i className='icon thirtytwo flex-noshrink'>{renderSectionIcon('devices')}</i>
          </figure>
        )}
        <h4 className={styles.sectionTitle}>
          Devices with embedded apps <span className={styles.comingSoon}>All devices coming soon</span>
        </h4>
      </header>
      <div
        className={`${styles.metricGrid} ${styles.threeColumnMetricGrid} ${styles.sectionContainer}`}
        data-testid='devices-tiles'
      >
        {mostUsed &&
          renderTile(
            {
              key: 'most-used-device',
              label: 'Most used device',
              value: mostUsed.name.replace('device\\', ''),
            },
            'sm'
          )}
        {leastUsed &&
          renderTile(
            {
              key: 'least-used-device',
              label: 'Least used device',
              value: leastUsed.name.replace('device\\', ''),
            },
            'sm'
          )}
        {renderTile(
          {
            key: 'total-pages-processed',
            label: 'Total pages processed',
            value: formatThousands(totalPages),
          },
          'sm'
        )}
      </div>
      <div className={`${styles.tableContainer} ${styles.sectionContainer}`}>
        <table className={styles.table} aria-label='Devices'>
          <thead>
            <tr>
              <th scope='col'>Name</th>
              <th scope='col'>Model</th>
              {(['Copy', 'Scan', 'Fax'] as const).map(jobType => (
                <th key={`${jobType}-total`} scope='col' className={styles.numericColumn}>
                  {jobType} total
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map(device => (
              <tr key={device.name}>
                <td>{device.name.replace('device\\', '')}</td>
                <td>{device.model}</td>
                {[
                  { key: 'copy', counts: device.copy },
                  { key: 'scan', counts: device.scan },
                  { key: 'fax', counts: device.fax },
                ].map(({ key, counts }) => (
                  <td key={`${device.name}-${key}-total`} className={styles.numericColumn}>
                    {formatThousands(counts.totalPages)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
